import React, { useState, useEffect, useContext } from 'react';
import {
  Container, Box, Typography, Accordion, AccordionSummary, AccordionDetails,
  TextField, Button, IconButton, Grid, Paper, Alert, Snackbar,
  Chip, Divider, Switch, FormControlLabel, Link, InputAdornment
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import AuthContext from '../context/AuthContext';
import axiosInstance from '../api/axiosConfig';

const daysOfWeek = [
  { label: 'Lundi', value: 1 },
  { label: 'Mardi', value: 2 },
  { label: 'Mercredi', value: 3 },
  { label: 'Jeudi', value: 4 },
  { label: 'Vendredi', value: 5 },
  { label: 'Samedi', value: 6 },
  { label: 'Dimanche', value: 7 },
];

const TaskEditor = ({ task, onSave, onDelete, allUsers }) => {
  const [localTask, setLocalTask] = useState(task);

  useEffect(() => {
    setLocalTask(task);
  }, [task]);

  const handleNameChange = (e) => {
    setLocalTask({ ...localTask, name: e.target.value });
  };

  const handleTimeChange = (dayValue, field, time) => {
    const existingSchedule = localTask.DefaultSchedules.find(ds => ds.dayOfWeek === dayValue);
    if (existingSchedule) {
      const updatedSchedules = localTask.DefaultSchedules.map(ds =>
        ds.dayOfWeek === dayValue ? { ...ds, [field]: time } : ds
      );
      setLocalTask({ ...localTask, DefaultSchedules: updatedSchedules });
    } else {
      const newSchedule = { dayOfWeek: dayValue, startTime: '00:00:00', endTime: '00:00:00', [field]: time };
      setLocalTask({ ...localTask, DefaultSchedules: [...localTask.DefaultSchedules, newSchedule] });
    }
  };

  const getDaySchedule = (dayValue) => {
    return localTask.DefaultSchedules.find(ds => ds.dayOfWeek === dayValue) || { startTime: '', endTime: '' };
  };

  const handleUserToggle = (userId) => {
    const defaultUsers = localTask.DefaultUsers || [];
    const currentIndex = defaultUsers.findIndex(u => u.id === userId);
    const newChecked = [...defaultUsers];

    if (currentIndex === -1) {
      newChecked.push({ id: userId });
    } else {
      newChecked.splice(currentIndex, 1);
    }
    setLocalTask({ ...localTask, DefaultUsers: newChecked });
  };

  return (
    <AccordionDetails>
      <Box>
        <TextField
          label="Nom de la tâche"
          value={localTask.name}
          onChange={handleNameChange}
          fullWidth
          variant="outlined"
          sx={{ mb: 2 }}
        />
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Horaires par défaut</Typography>
        <Grid container spacing={2}>
          {daysOfWeek.map(day => (
            <Grid key={day.value} size={{xs: 6, sm: 4, md: 4, lg: 3}}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography>{day.label}</Typography>
                <TextField
                  label="Début"
                  type="time"
                  value={getDaySchedule(day.value).startTime.substring(0, 5)}
                  onChange={(e) => handleTimeChange(day.value, 'startTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  margin="dense"
                />
                <TextField
                  label="Fin"
                  type="time"
                  value={getDaySchedule(day.value).endTime.substring(0, 5)}
                  onChange={(e) => handleTimeChange(day.value, 'endTime', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  margin="dense"
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Utilisateurs par défaut</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {[...allUsers].sort((a, b) => {
            const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase();
            const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase();
            if (nameA < nameB) return -1;
            if (nameA > nameB) return 1;
            return 0;
          }).map(user => {
            const isSelected = localTask.DefaultUsers?.some(du => du.id === user.id) || false;
            const displayName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
            return (
              <Chip
                key={user.id}
                label={displayName}
                onClick={() => handleUserToggle(user.id)}
                color={isSelected ? 'success' : 'default'}
              />
            );
          })}
        </Box>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="contained" onClick={() => onSave(localTask, localTask.DefaultUsers.map(u => u.id))}>Enregistrer</Button>
          {localTask.id && (
            <IconButton color="error" onClick={() => onDelete(localTask.id)}>
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      </Box>
    </AccordionDetails>
  );
};

const CompanySettings = () => {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newTaskCounter, setNewTaskCounter] = useState(0);
  const [expandedAccordion, setExpandedAccordion] = useState(false);
  const [isOrderChanged, setIsOrderChanged] = useState(false);
  const [notification, setNotification] = useState('');

  const [publicSettings, setPublicSettings] = useState({ isPublic: false, publicPassword: '' });
  const [showPublicPassword, setShowPublicPassword] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);

  const { activeCompany } = useContext(AuthContext);

  const fetchTasks = async () => {
    if (!activeCompany) return;
    setLoading(true);
    try {
      const [tasksRes, usersRes] = await Promise.all([
        axiosInstance.get(`/task/${activeCompany.id}`),
        axiosInstance.get(`/company/${activeCompany.id}/users?status=active`)
      ]);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
      setPublicSettings({
        isPublic: activeCompany.isPublic || false,
        publicPassword: '' // Ne pas pré-remplir le mot de passe
      });
      setIsOrderChanged(false);
    } catch (err) {
      setError('Erreur lors du chargement des tâches.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [activeCompany]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const handleSaveTask = async (taskToSave, defaultUserIds) => {
    try {
      const response = await axiosInstance.post('/task', {
        companyId: activeCompany.id,
        task: taskToSave,
        defaultUserIds: defaultUserIds,
      });
      if (taskToSave.id) {
        // Update existing
        setTasks(tasks.map(t => t.id === taskToSave.id ? response.data : t));
      } else {
        // Add new: remove the temporary task and add the new one from the response
        setTasks(tasks.filter(t => t.id !== taskToSave.id).concat(response.data));
      }
      setExpandedAccordion(false); // Close accordion after saving
    } catch (err) {
      setError('Erreur lors de la sauvegarde de la tâche.');
    }
  };

  const handleSaveOrder = async () => {
    const orderedTaskIds = tasks.map(t => t.id);
    try {
      await axiosInstance.post('/task/order', {
        companyId: activeCompany.id,
        orderedTaskIds,
      });
      setIsOrderChanged(false);
      setNotification('Ordre des tâches sauvegardé.');
    } catch (err) {
      setError("Erreur lors de la sauvegarde de l'ordre.");
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(tasks);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setTasks(items);
    setIsOrderChanged(true);
  };

  const handleDeleteTask = async (taskId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette tâche et tous ses horaires par défaut ?")) {
      try {
        await axiosInstance.delete(`/task/${activeCompany.id}/${taskId}`);
        setExpandedAccordion(false); // Close accordion if it was the one deleted
        setTasks(tasks.filter(t => t.id !== taskId));
      } catch (err) {
        setError('Erreur lors de la suppression de la tâche.');
      }
    }
  };

  const handleAddNewTask = () => {
    const newId = `new-${newTaskCounter}`;
    const newTask = {
      id: newId,
      name: 'Nouvelle tâche',
      DefaultSchedules: [],
      DefaultUsers: [],
    };
    setTasks([...tasks, newTask]);
    setNewTaskCounter(prev => prev + 1);
    setExpandedAccordion(newId); // Open the new accordion immediately
  };

  const handleIsPublicToggle = async (event) => {
    const isChecked = event.target.checked;
    setPublicSettings(prev => ({ ...prev, isPublic: isChecked }));
    try {
      await axiosInstance.put(`/company/${activeCompany.id}/settings`, { isPublic: isChecked });
      setNotification(`Page publique ${isChecked ? 'activée' : 'désactivée'}.`);
      if (!isChecked) {
        setShowPasswordChange(false); // Hide password field if page is disabled
      }
    } catch (err) {
      setError('Erreur lors de la mise à jour du statut de la page publique.');
      // Revert state on error
      setPublicSettings(prev => ({ ...prev, isPublic: !isChecked }));
    }
  };

  const handlePasswordChange = (event) => {
    setPublicSettings(prev => ({ ...prev, publicPassword: event.target.value }));
  };

  const handleSavePassword = async () => {
    if (!publicSettings.publicPassword) {
      setError("Le mot de passe ne peut pas être vide.");
      return;
    }
    try {
      await axiosInstance.put(`/company/${activeCompany.id}/settings`, { publicPassword: publicSettings.publicPassword });
      setNotification('Mot de passe de la page publique mis à jour.');
      setShowPasswordChange(false); // Hide after saving
      setPublicSettings(prev => ({ ...prev, publicPassword: '' })); // Clear field
    } catch (err) {
      setError('Erreur lors de la sauvegarde du mot de passe.');
    }
  };

  return (
    <Container maxWidth="lg">
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Typography>Chargement...</Typography>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>Page Publique</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Activez une page publique protégée par mot de passe pour partager le planning avec des personnes externes.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={publicSettings.isPublic}
                  onChange={handleIsPublicToggle}
                  name="isPublic"
                />
              }
              label={publicSettings.isPublic ? "Page publique activée" : "Page publique désactivée"}
            />
            {publicSettings.isPublic && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  URL publique :{' '}
                  <Link href={`/public/${activeCompany.publicId}`} target="_blank" rel="noopener noreferrer">
                    {`${window.location.origin}/public/${activeCompany.publicId}`}
                  </Link>
                </Typography>

                {!showPasswordChange ? (
                  <Link component="button" variant="body2" onClick={() => setShowPasswordChange(true)} sx={{ mt: 1 }}>
                    Changer le mot de passe de la page
                  </Link>
                ) : (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      label="Nouveau mot de passe public"
                      type={showPublicPassword ? 'text' : 'password'}
                      name="publicPassword"
                      value={publicSettings.publicPassword}
                      onChange={handlePasswordChange}
                      fullWidth
                      margin="normal"
                      helperText="Définissez un nouveau mot de passe pour la page publique."
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPublicPassword(show => !show)}
                              onMouseDown={(e) => e.preventDefault()}
                              edge="start"
                            >
                              {showPublicPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                      <Button variant="contained" onClick={handleSavePassword}>
                        Enregistrer le mot de passe
                      </Button>
                      <Button onClick={() => setShowPasswordChange(false)}>
                        Annuler
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Paper>

          <Typography variant="h6" gutterBottom>Tâches et Horaires</Typography>
          <Droppable droppableId="tasks">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {tasks.map((task, index) => (
                  <Draggable key={task.id} draggableId={String(task.id)} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          ...provided.draggableProps.style,
                          marginBottom: '8px',
                          boxShadow: snapshot.isDragging ? '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)' : 'none',
                        }}
                      >
                        <Accordion
                          expanded={expandedAccordion === task.id}
                          onChange={handleAccordionChange(task.id)}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box {...provided.dragHandleProps} sx={{ display: 'flex', alignItems: 'center', mr: 1, cursor: 'grab' }}>
                              <DragIndicatorIcon color="disabled" />
                            </Box>
                            <Typography>{task.name}</Typography>
                          </AccordionSummary>
                          <TaskEditor task={task} onSave={handleSaveTask} onDelete={handleDeleteTask} allUsers={users} />
                        </Accordion>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            {isOrderChanged && (
              <Button onClick={handleSaveOrder} variant="contained" color="secondary">
                Enregistrer l'ordre
              </Button>
            )}
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddNewTask}
              variant="outlined"
            >
              Ajouter une tâche
            </Button>
          </Box>
        </DragDropContext>
      )}
      <Snackbar
        open={!!notification}
        autoHideDuration={4000}
        onClose={() => setNotification('')}
        message={notification}
      />
    </Container>
  );
};

export default CompanySettings;